<template>
  <div
    class="relative px-4 py-6"
    :class="{
      'px-4 py-6': hazard?.hazardType !== HazardType.TotalFireBans,
    }"
  >
    <div class="absolute right-0 top-0 flex pl-2 pr-4 pt-6">
      <button class="ml-auto flex size-6 min-h-6 min-w-6 cursor-pointer items-center justify-center" @click.stop="emit('close')">
        <close-rounded class="*:fill-type-primary" aria-label="Close" />
      </button>
    </div>
    <template v-if="selectedHazardEntity">
      <ui-map-incident-popup
        v-if="selectedHazardEntity.entityType === HazardType.Incident"
        :show-footer="false"
        :hide-close-button="true"
      />
      <ui-map-prescribed-burn-popup
        v-else-if="selectedHazardEntity.entityType === HazardType.PrescribedBurns"
        :show-footer="false"
        :hide-close-button="true"
      />
      <ui-fire-ban-popup
        v-else-if="selectedHazardEntity.entityType === HazardType.TotalFireBans"
        :show-footer="false"
        :as-modal="true"
        :hide-close-button="true"
      />
      <ui-event-summary-content v-else-if="hazard" :content="event?.eventSummary" />
    </template>

    <template v-else>
      <ui-empty-hazard />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted } from 'vue';
import { EventEntity, HazardEntity, HazardType, SelectedHazard, useHazardsStore } from '@/features/hazards';
import CloseRounded from '@/common/assets/icons/close-rounded.svg';
import { storeToRefs } from 'pinia';

const props = defineProps<{
  event?: EventEntity;
  hazard?: SelectedHazard;
}>();

const emit = defineEmits<(event: 'close') => void>();

const hazardsStore = useHazardsStore();
const { hazards } = storeToRefs(hazardsStore);

/**
 * This interacts with the ui-event-summary-content component to
 * initialise the click event handlers for all the accordion headers
 * to toggle the visibility of the accordion content.
 */
const initialiseAccordions = () => {
  const accordionInnerElements = document.querySelectorAll('.accordion__inner');
  accordionInnerElements.forEach((accordionInnerElement) => {
    const accordionHeaderElement = accordionInnerElement.querySelector('.accordion__header');
    const accordionHeaderCompactElement = accordionInnerElement.querySelector('.accordion__header--compact');

    const clickHandler = () => {
      const accordionContentElement = accordionInnerElement.querySelector('.accordion__content');
      const accordionToggleElements = accordionInnerElement.querySelectorAll('.accordion__toggle');

      accordionContentElement?.classList?.toggle('accordion__content--off');
      accordionToggleElements.forEach((item) => item.classList?.toggle('accordion__toggle--off'));

      if (accordionHeaderElement) {
        accordionHeaderElement.ariaExpanded = accordionHeaderElement.ariaExpanded === 'true' ? 'false' : 'true';
      }

      if (accordionHeaderCompactElement) {
        accordionHeaderCompactElement.ariaExpanded = accordionHeaderCompactElement.ariaExpanded === 'true' ? 'false' : 'true';
      }

      // We can only get the scroll and client height after the element has been rendered, so we reinitialise the read more buttons *after* the dom update when the accordion is expanded
      nextTick(() => initialiseReadMore());
    };

    if (accordionHeaderElement) {
      accordionHeaderElement.addEventListener('click', clickHandler);
      accordionHeaderElement.ariaExpanded = 'false';
    }

    if (accordionHeaderCompactElement) {
      accordionHeaderCompactElement.addEventListener('click', clickHandler);
      accordionHeaderCompactElement.ariaExpanded = 'false';
    }
  });
};

/**
 * This interacts with the ui-event-summary-content component to
 * initialise the read more buttons for the content that has been clamped.
 * The read more button will toggle the visibility of the clamped content.
 * This function is copied from kudo-app.
 */
const initialiseReadMore = () => {
  const readMore = document.getElementsByClassName('read-more');
  if (!readMore) return;

  for (let i = 0; i < readMore.length; i++) {
    const readMoreClamped = readMore[i].firstElementChild;
    if (!readMoreClamped) return;

    if (readMoreClamped.scrollHeight > readMoreClamped.clientHeight && !readMore[i].classList.contains('read-more__button--added')) {
      // Ensures read more buttons are only ever added once
      readMore[i].classList.add('read-more__button--added');

      const readMoreButton = document.createElement('button');
      readMoreButton.innerText = 'Read more...';
      readMoreButton.title = 'Expand section text';
      readMoreButton.classList.add('read-more__button');
      readMore[i].appendChild(readMoreButton);

      readMoreButton.onclick = (e: MouseEvent) => {
        e.preventDefault();
        const target = e.target as HTMLElement;
        const contentEl = target.previousElementSibling;
        contentEl?.classList.toggle('read-more__clamped');
        target.title = contentEl?.classList.contains('read-more__clamped') ? 'Expand section text' : 'Collapse section text';
        target.innerText = contentEl?.classList.contains('read-more__clamped') ? 'Read more...' : 'Show less';
      };
    }
  }
};

/**
 * This interacts with the ui-event-summary-content component to expand
 * the accordion of the selected hazard.
 */
const expandSelectedHazardAccordion = () => {
  const hazardId = props.hazard?.hazardId;

  if (hazardId) {
    const hazardAccordionElement = document.querySelector('.container')?.querySelector(`[id="${hazardId}"]`);

    if (hazardAccordionElement) {
      const accordionInnerElement = hazardAccordionElement.querySelector('.accordion__inner');
      const accordionHeaderElement = hazardAccordionElement.querySelector('.accordion__header');

      if (accordionHeaderElement) {
        accordionHeaderElement.ariaExpanded = 'true';
      }

      accordionInnerElement?.querySelector('.accordion__content')?.classList?.toggle('accordion__content--off');
      accordionInnerElement?.querySelectorAll('.accordion__toggle').forEach((item) => item.classList?.toggle('accordion__toggle--off'));
    }
  }
};

const selectedHazardEntity = computed(() => {
  if (!props.hazard || !props.hazard?.hazardType?.length || !props.hazard?.hazardId?.length) {
    return null;
  }

  const selectedHazardEntity = (hazards.value[props.hazard?.hazardType] as HazardEntity[])?.find(
    (h: HazardEntity) => h.id === props.hazard?.hazardId
  ) as HazardEntity;

  return selectedHazardEntity;
});

onMounted(() => {
  initialiseAccordions();
  expandSelectedHazardAccordion();
  initialiseReadMore();
});
</script>
<style>
.title__issued-by {
  max-width: 95%;
}
</style>
